import { useTelegram } from '../hooks/useTelegram';
import { isImpersonating } from './api';

// Жестко указываем адрес твоего API
const BASE_URL = 'https://scalpex.space/api';

async function fetchWithInitData(endpoint, options = {}) {
  const tg = window.Telegram?.WebApp;
  let initData = tg?.initData || '';

  const headers = {
    'Content-Type': 'application/json',
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    // Режим юзера: так же, как api.js, добавляем X-Impersonate к КАЖДОМУ
    // запросу ботов. Без этого запросы botApi (создание виртуального бота,
    // статистика, ставка фандинга) уходили бы от лица АДМИНА, а не тестового
    // юзера — в режиме юзера всё ломалось (п.1 task.txt).
    ...(isImpersonating() ? { 'X-Impersonate': '1' } : {}),
    ...options.headers,
  };

  // endpoint у нас уже без /api (например, '/bots'), поэтому просто склеиваем
  const url = `${BASE_URL}${endpoint}`;

  console.log(`[BOT-API] Request to ${url}`, options.body);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[BOT-API] Error ${response.status}:`, errorText);
      throw new Error(`Server error ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log(`[BOT-API] Response from ${endpoint}:`, data);
    return data;
  } catch (err) {
    console.error(`[BOT-API] Network error:`, err);
    throw err;
  }
}

export const botApi = {
  // Создание бота (используем эндпоинт POST /api/bots)
  createBot: async (botName, params) => {
    // Количество ордеров: поддерживаем оба варианта имени свойства
    // (params.Token или params.Orders), в зависимости от того, как
    // форма создания бота передаёт это значение.
    const ordersCount = params.Token ?? params.Orders ?? params.orders ?? 1

    return fetchWithInitData('/bots', {
      method: 'POST',
      body: JSON.stringify({
        name: botName,
        takeProfit: params.TakeProfit,
        stopLoss: params.StopLoss,
        volume: params.Volume,
        leverage: params.Leverage,
        // ВАЖНО: раньше это поле вообще не отправлялось, из-за чего
        // Token в Controller_Param.json всегда падал на дефолт (4)
        // или на старое значение при создании бота.
        token: ordersCount,
        orders: ordersCount, // дублируем под именем, которое ждёт update-эндпоинт
        tradingMode: params.TradingMode ?? params.tradingMode ?? 'safe',
        // Направление сделки: LONG (сбор фандинга) / SHORT (сбор падений после выплаты)
        tradeMode: params.TradeDirection === 'SHORT' ? 'short' : 'long',
        isDemo: params.DEMO === "True",
        blacklist: params.Blacklist || [],
        // Передаем порог фандинга (по умолчанию -0.6 для отрицательных значений)
        minFundingRate: params.MinFundingRate ?? -0.6,
        // Макс. ставка финансирования (task.txt, 2026-09-16): необязательная
        // НИЖНЯЯ граница диапазона. null = ограничения нет.
        maxFundingRate: params.MaxFundingRate ?? null,
        // Биржа бота (task.txt, 2026-09-17): Binance — по схеме Bybit.
        // Сервер нормализует в lowercase-id и пишет display-имя в bots.exchange.
        exchange: params.Exchange ?? 'Bybit'
      }),
    });
  },

  // Получение списка ботов (GET /api/bots)
  getBots: async () => {
    return fetchWithInitData('/bots', { method: 'GET' });
  },

  // Создание «ПРОБНОГО» виртуального бота (Волна 1). Бот создаётся сразу
  // с фиксированными параметрами (объём ~300 USDT, порог −0.3%, стоп-лосс 0.1%,
  // направление от админа) — без модалки, без API-ключей и без подписки.
  // Порог и стоп-лосс заданы жёстко в api.js (TRIAL_BOT_MIN_FUNDING_RATE /
  // TRIAL_BOT_STOP_LOSS) и от админских дефолтов не зависят.
  createVirtualBot: async () => {
    return fetchWithInitData('/bots', {
      method: 'POST',
      body: JSON.stringify({ isVirtual: true }),
    });
  },

  // Запуск бота (POST /api/bots/:id/start)
  startBot: async (botId) => {
    return fetchWithInitData(`/bots/${botId}/start`, { method: 'POST' });
  },

  // Остановка бота (POST /api/bots/:id/stop)
  stopBot: async (botId) => {
    return fetchWithInitData(`/bots/${botId}/stop`, { method: 'POST' });
  },

  // Обновление параметров бота (POST /api/bots/:id/update)
  updateBot: async (botId, params) => {
    const payload = {
      leverage: params.leverage,
      orders: params.orders,
      globalVolume: params.globalVolume,
      tp: params.tp,
      sl: params.sl,
      tradingMode: params.tradingMode,
      minFundingRate: params.minFundingRate,
      // Макс. ставка финансирования (task.txt, 2026-09-16): null = ограничения нет
      maxFundingRate: params.maxFundingRate ?? null
    };
    console.log('[BOT-API] Updating bot', botId, payload);

    return fetchWithInitData(`/bots/${botId}/update`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Удаление бота (DELETE /api/bots/:id)
  deleteBot: async (botId) => {
    return fetchWithInitData(`/bots/${botId}`, { method: 'DELETE' });
  },

  // Получение статистики бота (GET /api/bots/:id/stats)
  getBotStats: async (botId) => {
    return fetchWithInitData(`/bots/${botId}/stats`, { method: 'GET' });
  },

  // Синхронизация PNL файлов для Дневника (POST /api/diary/sync)
  syncDiary: async () => {
    return fetchWithInitData('/diary/sync', { method: 'POST' });
  },

  // Актуальная ставка фандинга по паре (GET /api/funding-rate/:pair)
  // Используется карточкой бота вместо фейковых MOCK_SIGNALS.
  getFundingRate: async (pair) => {
    return fetchWithInitData(`/funding-rate/${encodeURIComponent(pair)}`, { method: 'GET' });
  },
};
